import { useQuery } from "@tanstack/react-query";

import { MONITORING_SERVICE } from "@apis/monitoring/config";
import type { HealthProbe, HealthProbeStatus } from "@apis/monitoring/models";

import { useUnauthenticatedQueries } from "../../../shared/hooks/useUnauthenticatedQueries.ts";

export function useHealthProbes({
  limit,
  target,
}: {
  readonly limit: number;
  readonly target: string;
}): readonly (HealthProbeStatus | null)[] {
  const FETCH_ALLOWANCE = 10;
  const { getHealthProbesQueryOptions } = useUnauthenticatedQueries();
  const healthProbes = useQuery(
    getHealthProbesQueryOptions({
      limit: limit + FETCH_ALLOWANCE,
      order: "desc",
      target,
    }),
  );

  return fillGaps(
    healthProbes.data?.items ?? [],
    limit,
    new Date(healthProbes.dataUpdatedAt),
  );
}

function fillGaps(
  probes: readonly HealthProbe[],
  limit: number,
  now: Date,
): readonly (HealthProbeStatus | null)[] {
  const latestTime = getLatestTime(now);

  return Array.from({ length: limit }, (_, i) => {
    const expectedTime =
      latestTime.getTime() - i * MONITORING_SERVICE.PROBE_INTERVAL.toMillis();

    const probe = probes.find(p => {
      const time = new Date(p.createdAt).getTime();

      return (
        Math.abs(time - expectedTime) <=
        MONITORING_SERVICE.PROBE_MATCH_TOLERANCE.toMillis()
      );
    });

    return probe?.status ?? null;
  });
}

function getLatestTime(now: Date): Date {
  const latestTime = new Date(now);

  const { PROBE_INTERVAL } = MONITORING_SERVICE;
  const minutes =
    Math.floor(now.getMinutes() / PROBE_INTERVAL.as("minutes")) *
    PROBE_INTERVAL.as("minutes");

  latestTime.setMinutes(minutes, 0, 0);

  return latestTime;
}
